/*
 * ---------------------------------------------------------------------------
 * LEOTUDE: bundle progress in the cart.
 *
 * Tells the shopper how far they are from the next multi-buy price, and offers
 * matching tees they can add without leaving the cart.
 *
 * IMPORTANT — this file only DISPLAYS the offer. Shopify owns the cart total,
 * so the actual price drop has to come from a discount app or a Shopify
 * Function configured with the same three tiers. If that isn't set up, this
 * widget promises a saving the checkout won't honour, which is worse than
 * showing nothing at all. Set the discount up first, then add this.
 *
 * Lives in assets/ rather than inside the cart snippet because every byte a
 * section renders counts against Shopify's per-section memory cap, and this
 * theme has already hit that ceiling once on main-product.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  /* -------------------------------------------------------------------------
   * CONFIG — the only part you should need to touch.
   *
   * Prices are in paise, the way Shopify's /cart.js reports them: ₹399 = 39900.
   * Each tier lists what a pack of that many costs in total.
   * ---------------------------------------------------------------------- */
  var TIERS = [
    { unit: 39900, label: 'Rs. 399', packs: { 2: 69900, 3: 99900 } },
    { unit: 49900, label: 'Rs. 499', packs: { 2: 89900, 3: 129900 } },
    { unit: 69900, label: 'Rs. 699', packs: { 2: 129900, 3: 189900 } },
    { unit: 99900, label: 'Rs. 999', packs: { 2: 179900, 3: 269900 } }
  ];

  // Extra safety net, OFF by default. A tier price on its own decides
  // eligibility, because product_type is inconsistently filled in and any
  // tee it fails to match gets miscounted — which is exactly how the widget
  // ended up asking for a fourth tee when three were already in the cart.
  // Only fill this in if you start selling non-tees at 399/699/499/999.
  var ELIGIBLE_TYPES = [];

  // Where "See all ... styles" points, per tier. Fill these in with your own
  // collection handles for the cleanest result. Left blank, it falls back to a
  // price-filtered /collections/all URL, which works but is uglier.
  // Collection handle per tier. Suggestions are pulled from here rather than
  // from Shopify's related-products API, which recommends on behaviour and was
  // offering women's tees against a men's cart. Anything in these collections
  // is the right price and bundle-eligible by definition.
  var TIER_COLLECTIONS = {
    39900: 'buy-2-for-699',
    69900: 'buy-2-for-1299',
    49900: 'buy-2-for-899',
    99900: 'buy-2-for-1799'
  };

  // "See all" link kahan le jaye, tier ke hisaab se. Sirf link aur uska text
  // badalta hai — suggestions ab bhi upar wale TIER_COLLECTIONS se aate hain,
  // taaki sirf sahi bhav wale products hi suggest hon.
  var TIER_LINKS = {
    39900: { url: '/collections/accessories', text: 'See all accessories' }
  };

  var MAX_SUGGESTIONS = 6;
  var STYLE_ID = 'lt-bundle-style';
  var DISMISS_KEY = 'lt-bundle-dismissed';
  var WIDGET_CLASS = 'lt-bundle';

  /* ---------------------------------------------------------------------- */

  var CSS = [
    /* The cart column this sits in is narrow, so everything here has to be
       able to shrink. Grid and flex children default to min-width: auto,
       which refuses to shrink below the content's natural width and pushes
       the Add button outside the border. min-width: 0 is what allows the
       long product title to ellipsis instead. */
    /* Every colour here is derived from the theme's own foreground/background
       variables, so light and dark mode both work with no extra rules. The
       !important on the anchors is not laziness: leotude-force-dark sets its
       own colours with !important, and without matching it these links fall
       back to the browser's default blue and purple, which is unreadable on
       the dark background. */
    '.lt-bundle, .lt-bundle * { box-sizing: border-box; }',
    '.lt-bundle a { color: rgb(var(--color-foreground)) !important; }',
    '.lt-bundle { position: relative; margin: 1.4rem 0;',
    '  padding: 2.6rem 1.4rem 1.4rem; border-radius: 0.8rem;',
    '  border: 0.1rem solid rgba(var(--color-foreground), 0.25);',
    '  background: rgb(var(--color-background)); overflow: hidden; }',
    '.lt-bundle__dismiss { position: absolute; top: 0.5rem; right: 0.5rem;',
    '  width: 2.2rem; height: 2.2rem; display: flex; align-items: center;',
    '  justify-content: center; padding: 0; cursor: pointer; border: 0;',
    '  border-radius: 50%; background: transparent;',
    '  color: rgba(var(--color-foreground), 0.6); }',
    '.lt-bundle__dismiss:hover { color: rgb(var(--color-foreground));',
    '  background: rgba(var(--color-foreground), 0.1); }',
    '.lt-bundle__headline { margin: 0;',
    '  font-size: 1.4rem; line-height: 1.45;',
    '  color: rgb(var(--color-foreground)); }',
    '.lt-bundle__save { font-weight: 600; white-space: nowrap; }',
    '.lt-bundle__track { display: flex; gap: 0.4rem; margin: 1.1rem 0 0.6rem; }',
    '.lt-bundle__seg { flex: 1 1 0; height: 0.7rem; border-radius: 0.35rem;',
    '  background: rgba(var(--color-foreground), 0.18);',
    '  transition: background-color 0.3s ease; }',
    '.lt-bundle__seg[data-filled="true"] { background: rgb(var(--color-foreground)); }',
    '.lt-bundle__steps { display: flex; justify-content: space-between; gap: 0.8rem;',
    '  margin: 0; font-size: 1.1rem; line-height: 1.3;',
    '  color: rgba(var(--color-foreground), 0.8); }',
    '.lt-bundle__steps span { white-space: nowrap; }',
    '.lt-bundle__steps span[data-reached="true"] { color: rgb(var(--color-foreground)); font-weight: 600; }',
    '.lt-bundle__shelf { margin: 1.4rem 0 0; min-width: 0; }',
    '.lt-bundle__shelfhead { display: flex; align-items: center; justify-content: space-between;',
    '  gap: 1rem; margin-bottom: 0.8rem; }',
    '.lt-bundle__shelftitle { margin: 0; font-size: 1.3rem; font-weight: 600; }',
    '.lt-bundle__arrows { display: flex; gap: 0.4rem; }',
    '.lt-bundle__arrow { width: 2.4rem; height: 2.4rem; display: flex; align-items: center;',
    '  justify-content: center; cursor: pointer; padding: 0; border-radius: 50%;',
    '  background: transparent; color: rgb(var(--color-foreground));',
    '  border: 0.1rem solid rgba(var(--color-foreground), 0.4); }',
    '.lt-bundle__arrow[disabled] { opacity: 0.3; cursor: default; }',
    /* Horizontal strip with snap, one card per view. Overflow-x on a flex row
       is what lets this work without a carousel library — the arrows just
       scroll it. */
    '.lt-bundle__suggest { display: flex; gap: 1rem; margin: 0; padding: 0;',
    '  list-style: none; overflow-x: auto; scroll-snap-type: x mandatory;',
    '  scrollbar-width: none; -ms-overflow-style: none; }',
    '.lt-bundle__suggest::-webkit-scrollbar { display: none; }',
    '.lt-bundle__item { flex: 0 0 100%; scroll-snap-align: start;',
    '  display: flex; align-items: center; gap: 1rem; min-width: 0;',
    '  padding: 0.8rem; border-radius: 0.6rem;',
    '  background: rgb(var(--color-background));',
    '  border: 0.1rem solid rgba(var(--color-foreground), 0.22); }',
    '.lt-bundle__sizes { display: flex; flex-wrap: wrap; gap: 0.4rem; margin-top: 0.6rem; }',
    '.lt-bundle__size { padding: 0.3rem 0.7rem; font-size: 1.1rem; cursor: pointer;',
    '  border-radius: 0.3rem; background: transparent; color: rgb(var(--color-foreground));',
    '  border: 0.1rem solid rgba(var(--color-foreground), 0.35); }',
    '.lt-bundle__size:hover { border-color: rgb(var(--color-foreground)); }',
    '.lt-bundle__size[disabled] { opacity: 0.35; cursor: default; text-decoration: line-through; }',
    '.lt-bundle__thumb { flex: 0 0 4rem; width: 4rem; height: 5rem; object-fit: cover;',
    '  border-radius: 0.4rem; }',
    '.lt-bundle__meta { flex: 1 1 auto; min-width: 0; }',
    '.lt-bundle__name { display: block; font-size: 1.3rem; line-height: 1.35;',
    '  text-decoration: none;',
    '  overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }',
    '.lt-bundle__name:hover { text-decoration: underline; }',
    '.lt-bundle__price { display: block; font-size: 1.2rem; margin-top: 0.2rem;',
    '  color: rgba(var(--color-foreground), 0.8); }',
    '.lt-bundle__add { flex: 0 0 auto; display: inline-block; text-decoration: none;',
    '  font-family: inherit;',
    '  padding: 0.6rem 1.2rem; font-size: 1.2rem;',
    '  cursor: pointer; border-radius: 0.4rem; background: transparent;',
    '  white-space: nowrap;',
    '  border: 0.1rem solid rgba(var(--color-foreground), 0.6);',
    '  color: rgb(var(--color-foreground)); }',
    '.lt-bundle__add:hover { border-color: rgb(var(--color-foreground)); }',
    '.lt-bundle__link { display: inline-block; margin-top: 1.2rem; font-size: 1.3rem;',
    '  text-decoration: underline; }',
    /* The drawer is short and the checkout button has to stay reachable, so
       the panel gives up padding and the strip loses its heading there. */
    'cart-drawer .lt-bundle__shelf { margin-top: 1rem; }',
    'cart-drawer .lt-bundle__thumb { flex: 0 0 3.4rem; width: 3.4rem; height: 4.2rem; }',
    '@media (prefers-reduced-motion: reduce) { .lt-bundle__seg { transition: none; } }',

    /* ---------------------------------------------------------------------
     * Drawer repairs for light mode.
     *
     * This theme keeps dark as the default and builds light mode by writing
     * explicit [data-theme="light"] overrides per component — mobile nav,
     * cart notification, search overlay and so on all have their own block in
     * theme.liquid. It never redefines --color-background or
     * --color-foreground, so those variables hold the DARK scheme's values in
     * both modes.
     *
     * That's why only light mode looked wrong: any rule written against those
     * variables painted dark-mode colours onto a light page. The drawer is
     * simply a component nobody wrote the light-mode block for, so this adds
     * it, in the same style as the rest of the file.
     * ------------------------------------------------------------------- */
    '[data-theme="light"] #CartDrawer,',
    '[data-theme="light"] .drawer__inner,',
    '[data-theme="light"] cart-drawer .drawer__footer {',
    '  background-color: #ffffff !important;',
    '  background-image: none !important;',
    '  color: #000000 !important; }',

    '[data-theme="light"] #CartDrawer .drawer__heading,',
    '[data-theme="light"] #CartDrawer .cart-item__name,',
    '[data-theme="light"] #CartDrawer .totals *,',
    '[data-theme="light"] #CartDrawer .cart-item__details * {',
    '  color: #000000 !important; }',

    /* The close and remove icons inherit currentColor, which was resolving to
       the dark scheme's near-white and disappearing against the panel. */
    '[data-theme="light"] #CartDrawer .drawer__close,',
    '[data-theme="light"] #CartDrawer cart-remove-button .button,',
    '[data-theme="light"] #CartDrawer cart-remove-button svg,',
    '[data-theme="light"] #CartDrawer .quantity__button {',
    '  color: #000000 !important; }',

    /* The remove icon carries its own stroke and fill, so setting color alone
       left it pale — currentColor never reaches it. */
    '[data-theme="light"] #CartDrawer cart-remove-button svg,',
    '[data-theme="light"] #CartDrawer cart-remove-button svg *,',
    '[data-theme="light"] #CartDrawer .cart-remove-button svg,',
    '[data-theme="light"] #CartDrawer .cart-remove-button svg *,',
    '[data-theme="light"] .drawer__inner cart-remove-button svg,',
    '[data-theme="light"] .drawer__inner cart-remove-button svg * {',
    '  stroke: #000000 !important; color: #000000 !important; }',
    '[data-theme="light"] #CartDrawer cart-remove-button svg [fill]:not([fill="none"]),',
    '[data-theme="light"] .drawer__inner cart-remove-button svg [fill]:not([fill="none"]) {',
    '  fill: #000000 !important; }',
    '[data-theme="light"] #CartDrawer cart-remove-button .button:before,',
    '[data-theme="light"] #CartDrawer cart-remove-button .button:after {',
    '  box-shadow: none !important; }',

    '[data-theme="light"] #CartDrawer .quantity,',
    '[data-theme="light"] #CartDrawer quantity-input {',
    '  background-color: #ffffff !important;',
    '  border: 0.1rem solid #000000 !important;',
    '  border-radius: 0.4rem !important; }',
    '[data-theme="light"] #CartDrawer .quantity__input {',
    '  background-color: transparent !important; color: #000000 !important; }',
    '[data-theme="light"] #CartDrawer .quantity:before,',
    '[data-theme="light"] #CartDrawer .quantity:after { box-shadow: none !important; }',

    /* The footer floats over the scrolling item list; without an opaque
       background the last product showed through the panel. */
    '[data-theme="light"] cart-drawer .drawer__footer {',
    '  border-top: 0.1rem solid rgba(0, 0, 0, 0.15) !important; }',
    'cart-drawer .drawer__footer { border-top: 0.1rem solid rgba(var(--color-foreground), 0.15); }',

    '[data-theme="light"] #CartDrawer-Overlay {',
    '  background-color: rgba(0, 0, 0, 0.35) !important; }',

    /* Widget panel: white with a black outline, as asked. */
    '[data-theme="light"] .lt-bundle {',
    '  background: #ffffff !important; border: 0.1rem solid #000000 !important;',
    '  color: #000000 !important; }',
    '[data-theme="light"] .lt-bundle__dismiss:hover { background: rgba(0, 0, 0, 0.08) !important; }',
    '[data-theme="light"] .lt-bundle *,',
    '[data-theme="light"] .lt-bundle a { color: #000000 !important; }',
    '[data-theme="light"] .lt-bundle__item {',
    '  background: #ffffff !important;',
    '  border: 0.1rem solid rgba(0, 0, 0, 0.25) !important; }',
    '[data-theme="light"] .lt-bundle__add,',
    '[data-theme="light"] .lt-bundle__arrow,',
    '[data-theme="light"] .lt-bundle__size {',
    '  border-color: #000000 !important; background: transparent !important; }',
    '[data-theme="light"] .lt-bundle__seg { background: rgba(0, 0, 0, 0.12) !important; }',
    '[data-theme="light"] .lt-bundle__seg[data-filled="true"] { background: #000000 !important; }',

    /* Dark mode keeps taking its colours from the scheme, which is correct
       there — those variables already hold the dark values. */
    /* The footer was overlapping the item list, so an opaque background just
       hid the last product's quantity controls instead of fixing anything.
       Making the drawer a column — scrolling list, footer below it — means
       nothing overlaps and nothing needs to be painted over. */
    'cart-drawer .drawer__inner { display: flex; flex-direction: column;',
    '  height: 100%; overflow: hidden; }',
    'cart-drawer .drawer__cart-items-wrapper, cart-drawer .cart-drawer__form {',
    '  flex: 1 1 auto; min-height: 0; overflow-y: auto; }',
    /* Making .drawer__inner a flex column took Dawn's own horizontal padding
       out of play, so the totals row ran past the panel edge and clipped
       "INR". Both children get it back explicitly. */
    'cart-drawer .drawer__footer { flex: 0 0 auto; position: static;',
    '  padding-left: 1.5rem !important; padding-right: 1.5rem !important;',
    '  background-color: rgb(var(--color-background)); }',
    'cart-drawer .drawer__cart-items-wrapper {',
    '  padding-left: 1.5rem; padding-right: 1.5rem; }',
    'cart-drawer .totals { flex-wrap: wrap; gap: 0.4rem; }',
    'cart-drawer .totals__subtotal-value { white-space: nowrap; }',

    /* The mobile nav is fixed to the bottom of the viewport and sits above the
       drawer, so CHECK OUT was disappearing behind it once the widget made
       the footer taller. Reserving that strip keeps the button reachable —
       the nav height plus the phone's own home-indicator inset. */
    '@media screen and (max-width: 749px) {',
    '  cart-drawer .drawer__footer {',
    '    padding-bottom: calc(7rem + env(safe-area-inset-bottom)) !important; }',
    '  cart-drawer .drawer__inner { padding-bottom: 0 !important; }',
    '}',
    '#CartDrawer, .drawer__inner {',
    '  background-color: rgb(var(--color-background)) !important;',
    '  background-image: none !important; }',
    '#CartDrawer-Overlay { background-color: rgba(0, 0, 0, 0.35) !important; }'
  ].join('\n');

  function injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  /* The theme prints "Rs. 399.00", so this does too. Two currency formats on
     one page reads like a bolted-on app. */
  function money(paise) {
    return 'Rs. ' + (paise / 100).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  function tierFor(item) {
    var match = null;
    TIERS.forEach(function (tier) {
      if (item.price === tier.unit) match = tier;
    });
    if (!match) return null;

    if (!ELIGIBLE_TYPES.length) return match;

    var type = (item.product_type || '').toLowerCase();
    var ok = ELIGIBLE_TYPES.some(function (word) {
      return type.indexOf(word) !== -1;
    });
    return ok ? match : null;
  }

  /*
   * Packs of 3 are better value per tee than packs of 2 at every tier, so fill
   * threes first and let the remainder form a two. A leftover of 1 or 2 is what
   * the shopper gets prompted about.
   */
  function analyse(cart) {
    var counts = {};
    var owned = {};

    cart.items.forEach(function (item) {
      var tier = tierFor(item);
      if (!tier) return;
      counts[tier.unit] = (counts[tier.unit] || 0) + item.quantity;
      owned[item.product_id] = true;
    });

    var best = null;

    TIERS.forEach(function (tier) {
      var qty = counts[tier.unit];
      if (!qty) return;

      var remainder = qty % 3;
      if (remainder === 0) return; // every tee is already in a pack of 3

      var needed, saving, target;

      if (remainder === 1) {
        // One loose tee: one more makes a pack of 2.
        needed = 1;
        target = 2;
        saving = tier.unit * 2 - tier.packs[2];
      } else {
        // Two loose tees: one more upgrades them to a pack of 3.
        needed = 1;
        target = 3;
        saving = tier.unit * 3 - tier.packs[3] - (tier.unit * 2 - tier.packs[2]);
      }

      var candidate = {
        tier: tier,
        inCart: remainder,
        needed: needed,
        target: target,
        saving: saving,
        packPrice: tier.packs[target],
        owned: owned
      };

      // Prompt about whichever tier is closest to a pack; break ties on the
      // bigger saving.
      if (!best || candidate.saving > best.saving) best = candidate;
    });

    return best;
  }

  /* Pack pe kitna % bachta hai: (MRP-jod - pack price) / MRP-jod.
     Round karke dikhate hain, jaise 9.96% -> 10%. */
  function pctOff(tier, n) {
    var full = tier.unit * n;
    return Math.round((full - tier.packs[n]) / full * 100);
  }

  // Site jaisa chhota naam: "Leotude Men's ... Premium ..." -> "...".
  // leotude-short-title snippet wala hi niyam.
  function shortTitle(t) {
    var out = String(t || '');
    var pre = ["Leotude Men's ", "Leotude Women's ", 'Leotude Men\u2019s ', 'Leotude Women\u2019s ',
      'Leotude Mens ', 'Leotude Womens ', 'Leotude '];
    for (var i = 0; i < pre.length; i++) {
      if (out.indexOf(pre[i]) === 0) { out = out.slice(pre[i].length); break; }
    }
    return out.split('Premium ').join('');
  }

  function buildWidget(state) {
    var box = document.createElement('div');
    box.className = WIDGET_CLASS;

    /* Somewhere to put it down. An upsell that can't be closed stops being a
       suggestion. Dismissal lasts the session — not forever, since the offer
       is genuinely worth showing to a new visit, and not one page either,
       which would make the close button feel broken. */
    var dismiss = document.createElement('button');
    dismiss.type = 'button';
    dismiss.className = 'lt-bundle__dismiss';
    dismiss.setAttribute('aria-label', 'Dismiss offer');
    dismiss.innerHTML =
      '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" ' +
      'stroke-width="2.5" aria-hidden="true"><line x1="5" y1="5" x2="19" y2="19"/>' +
      '<line x1="19" y1="5" x2="5" y2="19"/></svg>';
    dismiss.addEventListener('click', function () {
      try {
        sessionStorage.setItem(DISMISS_KEY, '1');
      } catch (e) { /* private mode: it just reappears next render */ }
      box.remove();
    });
    box.appendChild(dismiss);

    var headline = document.createElement('p');
    headline.className = 'lt-bundle__headline';
    headline.setAttribute('aria-live', 'polite');
    // Price ki jagah percentage: "Add 1 more & get 10% OFF on 2 items"
    headline.innerHTML =
      'Add ' + state.needed + ' more &amp; get ' +
      '<span class="lt-bundle__save">' + pctOff(state.tier, state.target) + '% OFF</span>' +
      ' on ' + state.target + ' items';
    box.appendChild(headline);

    /* Three segments rather than one sliding bar. A percentage fill reads as
       "some progress"; three blocks read as "one of three", which is the
       actual offer. */
    var track = document.createElement('div');
    track.className = 'lt-bundle__track';
    for (var i = 1; i <= 3; i++) {
      var seg = document.createElement('span');
      seg.className = 'lt-bundle__seg';
      seg.setAttribute('data-filled', state.inCart >= i ? 'true' : 'false');
      track.appendChild(seg);
    }
    box.appendChild(track);

    var steps = document.createElement('p');
    steps.className = 'lt-bundle__steps';
    [1, 2, 3].forEach(function (n) {
      var span = document.createElement('span');
      span.textContent = n === 1 ? '1 item' : n + ' items · ' + pctOff(state.tier, n) + '% OFF';
      span.setAttribute('data-reached', state.inCart >= n ? 'true' : 'false');
      steps.appendChild(span);
    });
    box.appendChild(steps);

    return box;
  }

  function tierCollectionUrl(tier) {
    var handle = TIER_COLLECTIONS[tier.unit];
    if (handle) return '/collections/' + handle;

    var rupees = tier.unit / 100;
    return '/collections/all?filter.v.price.gte=' + (rupees - 1) +
      '&filter.v.price.lte=' + rupees;
  }

  function appendBrowseLink(box, state) {
    var link = document.createElement('a');
    link.className = 'lt-bundle__link';
    var custom = TIER_LINKS[state.tier.unit];
    link.href = custom ? custom.url : tierCollectionUrl(state.tier);
    link.textContent = (custom ? custom.text : 'See all ' + state.tier.label + ' styles') + ' \u2192';
    box.appendChild(link);
  }

  function shuffle(list) {
    for (var i = list.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var tmp = list[i];
      list[i] = list[j];
      list[j] = tmp;
    }
    return list;
  }

  /*
   * Adds a specific variant, then asks Dawn to re-render its own cart sections
   * so the drawer stays open and the totals update in place.
   */
  function addVariant(variantId, chip) {
    var sections = ['cart-drawer', 'cart-icon-bubble', 'main-cart-items', 'main-cart-footer'];

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: sections.join(',')
      })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var host = document.querySelector('cart-drawer') || document.querySelector('cart-items');
        if (host && data.sections && typeof host.renderContents === 'function') {
          host.renderContents(data);
          setTimeout(function () { render(true); }, 150);
        } else {
          window.location.reload();
        }
      })
      .catch(function () {
        chip.disabled = false;
        chip.textContent = chip.dataset.label;
      });
  }

  /*
   * Sizes appear only after the shopper taps Add. Showing every size up front
   * makes the strip unreadable in a narrow drawer, and adding without asking
   * would guess the size for them — which is how an upsell becomes a return.
   */
  function showSizes(meta, product) {
    if (meta.querySelector('.lt-bundle__sizes')) return;

    var wrap = document.createElement('div');
    wrap.className = 'lt-bundle__sizes';

    (product.variants || []).forEach(function (variant) {
      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'lt-bundle__size';
      chip.textContent = variant.title;
      chip.dataset.label = variant.title;

      if (!variant.available) {
        chip.disabled = true;
        chip.title = 'Out of stock';
      } else {
        chip.addEventListener('click', function () {
          chip.disabled = true;
          chip.textContent = '\u2026';
          addVariant(variant.id, chip);
        });
      }

      wrap.appendChild(chip);
    });

    meta.appendChild(wrap);
  }

  function buildShelf(state, picks) {
    var shelf = document.createElement('div');
    shelf.className = 'lt-bundle__shelf';

    var head = document.createElement('div');
    head.className = 'lt-bundle__shelfhead';

    var title = document.createElement('p');
    title.className = 'lt-bundle__shelftitle';
    title.textContent = 'Complete the offer with';
    head.appendChild(title);

    var arrows = document.createElement('div');
    arrows.className = 'lt-bundle__arrows';
    var prev = arrowButton('Previous', '15 18 9 12 15 6');
    var next = arrowButton('Next', '9 18 15 12 9 6');
    arrows.appendChild(prev);
    arrows.appendChild(next);
    head.appendChild(arrows);

    shelf.appendChild(head);

    var list = document.createElement('ul');
    list.className = 'lt-bundle__suggest';

    picks.forEach(function (product) {
      var href = '/products/' + product.handle;
      var image = (product.images || [])[0];

      var li = document.createElement('li');
      li.className = 'lt-bundle__item';

      var img = document.createElement('img');
      img.className = 'lt-bundle__thumb';
      img.src = image ? image.src : '';
      img.alt = '';
      img.loading = 'lazy';
      li.appendChild(img);

      var meta = document.createElement('div');
      meta.className = 'lt-bundle__meta';

      var name = document.createElement('a');
      name.className = 'lt-bundle__name';
      name.href = href;
      name.textContent = shortTitle(product.title);
      name.title = product.title; // hover pe poora naam
      meta.appendChild(name);

      var price = document.createElement('span');
      price.className = 'lt-bundle__price';
      price.textContent = state.tier.label;
      meta.appendChild(price);

      li.appendChild(meta);

      var cta = document.createElement('button');
      cta.type = 'button';
      cta.className = 'lt-bundle__add';
      cta.textContent = 'Add';
      cta.addEventListener('click', function () {
        cta.style.display = 'none';
        showSizes(meta, product);
      });
      li.appendChild(cta);

      list.appendChild(li);
    });

    shelf.appendChild(list);

    function sync() {
      prev.disabled = list.scrollLeft <= 4;
      next.disabled = list.scrollLeft + list.clientWidth >= list.scrollWidth - 4;
    }

    function scrollBy(direction) {
      list.scrollBy({ left: direction * list.clientWidth, behavior: 'smooth' });
      setTimeout(sync, 400);
    }

    prev.addEventListener('click', function () { scrollBy(-1); });
    next.addEventListener('click', function () { scrollBy(1); });
    list.addEventListener('scroll', sync);
    setTimeout(sync, 0);

    return shelf;
  }

  function arrowButton(label, points) {
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'lt-bundle__arrow';
    button.setAttribute('aria-label', label);
    button.innerHTML =
      '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" ' +
      'stroke-width="2.5" aria-hidden="true"><polyline points="' + points + '"/></svg>';
    return button;
  }

  function addSuggestions(box, state) {
    var handle = TIER_COLLECTIONS[state.tier.unit];
    if (!handle) {
      appendBrowseLink(box, state);
      return;
    }

    fetch('/collections/' + handle + '/products.json?limit=50')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var pool = (data.products || []).filter(function (product) {
          if (state.owned[product.id]) return false; // already in the cart
          return (product.variants || []).some(function (v) { return v.available; });
        });

        var picks = shuffle(pool).slice(0, MAX_SUGGESTIONS);
        if (!picks.length) return;

        box.appendChild(buildShelf(state, picks));
      })
      .catch(function () { /* the headline and browse link still work */ })
      .then(function () { appendBrowseLink(box, state); });
  }

  function mountPoint() {
    // Drawer first — that's what most shoppers see.
    return (
      document.querySelector('cart-drawer .drawer__footer') ||
      document.querySelector('.cart__footer .cart__blocks') ||
      document.querySelector('.cart__footer') ||
      null
    );
  }

  var busy = false;
  var lastSignature = null;

  function signature(cart) {
    return cart.items.map(function (item) {
      return item.variant_id + 'x' + item.quantity;
    }).join('|');
  }

  /*
   * Rebuild only when the cart's contents actually change.
   *
   * Without this the widget destroys itself in a loop: inserting the panel is
   * itself a DOM change, the observer reads that as "cart updated" and renders
   * again. The headline survives because it is built synchronously, but the
   * suggestions arrive from a fetch a moment later and land on a panel that
   * has already been thrown away — which is why nothing appeared below the
   * progress bar.
   */
  function render(force) {
    if (busy) return;
    var host = mountPoint();
    if (!host) return;

    busy = true;

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var sig = signature(cart);
        var existing = host.querySelector('.' + WIDGET_CLASS);

        var dismissed = false;
        try {
          dismissed = sessionStorage.getItem(DISMISS_KEY) === '1';
        } catch (e) { /* storage blocked; show it */ }

        if (dismissed) {
          if (existing) existing.remove();
          return;
        }

        if (!force && sig === lastSignature && existing) return;
        lastSignature = sig;

        if (existing) existing.remove();
        if (!cart.item_count) return;

        var state = analyse(cart);
        if (!state) return; // every eligible item is already in a full pack

        var box = buildWidget(state);
        host.insertBefore(box, host.firstChild);
        addSuggestions(box, state);
      })
      .catch(function () { /* leave the cart alone if this fails */ })
      .then(function () { busy = false; });
  }

  var timer = null;
  function scheduleRender() {
    clearTimeout(timer);
    timer = setTimeout(render, 200);
  }

  function watch() {
    injectStyle();
    render(true);

    // Dawn re-renders the cart section on every quantity change, add and
    // remove. Watching the DOM catches all of them without having to hook into
    // theme internals that change between versions.
    var target =
      document.querySelector('cart-drawer') ||
      document.querySelector('#main-cart-items') ||
      document.body;

    new MutationObserver(function (records) {
      var outside = records.some(function (record) {
        var node = record.target;
        var el = node.nodeType === 1 ? node : node.parentElement;
        return !(el && el.closest && el.closest('.' + WIDGET_CLASS));
      });
      if (outside) scheduleRender();
    }).observe(target, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watch);
  } else {
    watch();
  }

  document.addEventListener('shopify:section:load', scheduleRender);
})();